import type { Me } from './api'

// One account, one persona: which console screens are the signed-in persona's
// job.
//
// The list is the API's (GET /v1/me screens, from the persona catalogue in
// packages/core/domain), and this file holds no copy of it, for the reason
// capabilities are read the same way: two copies drift, and the person looking
// for a screen is the one who finds out.
//
// This is not an entitlement. What anybody may do is still decided by the
// capabilities of their roles, on the server. A screen outside the list is
// left out of the menu and, opened anyway, says it is somebody else's job.
//
// Absent means unfiltered. An API from before personas named screens, and an
// account whose roles derive no persona at all, answer with no list, and both
// keep the console exactly as it was rather than an empty menu.

let allowed: Set<string> | null = null
let persona = ''

export function loadScreens(me: Me): void {
  allowed = Array.isArray(me.screens) ? new Set(me.screens) : null
  persona = me.persona?.name ?? ''
}

// An address nothing answers to is nobody's job, so it is always shown.
export function onScreen(name: string): boolean {
  return name === 'not-found' || allowed === null || allowed.has(name)
}

// The name of the persona the screens belong to, for the sentence that says a
// screen is not part of it.
export function screensPersonaName(): string {
  return persona
}

// The screen a console address opens, for a link built somewhere other than the
// menu (a queue row on Home). It reads addresses the way App.tsx parseHash does
// for the shapes those links use; an address it does not recognise is its first
// segment, which is the route name for every list screen.
export function screenOfHref(href: string): string {
  const path = href.replace(/^#\/?/, '').split('?')[0] ?? ''
  const parts = path.split('/').filter(Boolean)
  const [first = '', id = '', rest = ''] = parts
  if (first === '') return 'home'
  if (first === 'exits' && id) return 'exit-workspace'
  if (first === 'vendor' && id === 'new') return 'vendor-add'
  if (first === 'signoffs') return 'my-work'
  if (first === 'vendor' && id && rest === 'invite') return 'vendor-invite'
  if (first === 'vendor' && id && rest === 'full') return 'vendor-record'
  if (first === 'questionnaire' && id === 'import') return 'questionnaire-import'
  if (first === 'settings' && (id === 'due-diligence' || id === 'exception-authority')) return id
  return first
}
